import { PayloadAction, createSlice, createSelector } from '@reduxjs/toolkit';
import { RootState } from '..';
import { PageConfigModel, AppConfig } from '../../models/page-config.models';

// identifier
export const CONFIG_SLICE_ID = 'config';

// default state
const initialState: PageConfigModel = {
  appConfig: <AppConfig>{},
  lastActiveModule: null,
  lastActiveProject: {},
};

//slice
const configSlice = createSlice({
  name: CONFIG_SLICE_ID,
  initialState,
  reducers: {
    setAppConfig: (state: PageConfigModel, action: PayloadAction<AppConfig>) => {
      state.appConfig = action.payload;
    },
    setLastActiveModule: (state: PageConfigModel, action: PayloadAction<number>) => {
      state.lastActiveModule = action.payload;
    },
    setLastActiveProject: (
      state: PageConfigModel,
      action: PayloadAction<{ moduleId: number; projectId: string }>
    ) => {
      const { moduleId, projectId } = action.payload;
      state.lastActiveProject = { ...state.lastActiveProject, [moduleId]: projectId };
    },
    clearConfig: (state: PageConfigModel) => {
      state.appConfig = initialState.appConfig;
      state.lastActiveModule = initialState.lastActiveModule;
      state.lastActiveProject = initialState.lastActiveProject;
    },
  },
});

//selectors
export const selectLastActiveModule = (state: RootState) => state.config.lastActiveModule;
export const selectLastActiveProjectByModuleId = createSelector(
  [
    (state: RootState) => state.config.lastActiveProject,
    (state: RootState, moduleId: number | null) => moduleId,
  ],
  (lastActiveProject, moduleId) => (moduleId ? lastActiveProject[moduleId] : undefined)
);

export const selectLastActiveProjects = createSelector(
  [(state: RootState) => state.config],
  (config: PageConfigModel) => config.lastActiveProject
);

export const selectKwelloUserId = (state: RootState) => state.config.appConfig.kwelloUserId;
export const selectExternalName = (state: RootState) => state.config.appConfig.fullName;
export const selectModules = (state: RootState) => state.config.appConfig.modules;

export const selectCompanies = createSelector(
  [(state: RootState) => state.config.appConfig],
  (appConfig: AppConfig) =>
    appConfig.modules.reduce((companies: string[], module) => {
      const moduleCompanies = module.projects.map((project) => project.projectClientName);

      moduleCompanies.forEach((company) => {
        if (!companies.includes(company)) {
          companies.push(company);
        }
      });
      return companies?.slice().sort((a, b) => a.localeCompare(b));
    }, [])
);

//actions
export const { setAppConfig, setLastActiveModule, setLastActiveProject, clearConfig } =
  configSlice.actions;

//reducer
export default configSlice;
